import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import requestService from '../services/requestService';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';

function ManageRequestsPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'pending', 'approved', 'rejected', 'fulfilled', 'canceled'
  
  // Fetch recipient requests from API
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (user?.role === 'recipient') {
        const response = await requestService.getMyRecipientRequests();
        console.log('Recipient requests:', response);
        
        // Correctly handle the nested response structure based on actual API response
        if (Array.isArray(response)) {
          // If response is already an array of requests
          setRequests(response);
        } else if (response?.requests?.results && Array.isArray(response.requests.results)) {
          // If response has the structure { requests: { results: [...] } }
          console.log('Found requests at response.requests.results:', response.requests.results);
          setRequests(response.requests.results);
        } else if (response?.requests && Array.isArray(response.requests)) {
          // If response has the structure { requests: [...] }
          setRequests(response.requests);
        } else if (response?.results && Array.isArray(response.results)) {
          // If response has the structure { results: [...] }
          setRequests(response.results);
        } 
        // Handle the inconsistent case with non-empty pagination but empty requests array
        else if (response?.requests && Array.isArray(response.requests) && 
                 response.requests.length === 0 && 
                 response.pagination?.total > 0) {
          console.warn('API returned empty requests array but pagination shows records exist');
          // Use demo data in this case (if we had a getDemoRecipientRequests function)
          // For now, just show empty state
          setRequests([]);
        } 
        // Handle empty array structure at requests.results with non-zero pagination
        else if (response?.requests?.results && Array.isArray(response.requests.results) && 
                 response.requests.results.length === 0 && 
                 (response.pagination?.total > 0 || response.requests.pagination?.total > 0)) {
          console.warn('API returned empty requests.results array but pagination shows records exist');
          setRequests([]);
        }
        else if (response && typeof response === 'object') {
          // Try to find any array in the response that might contain requests
          const findArrayInObject = (obj) => {
            for (const key in obj) {
              if (Array.isArray(obj[key]) && obj[key].length > 0 && obj[key][0]?.id) {
                return obj[key];
              } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                const result = findArrayInObject(obj[key]);
                if (result) return result;
              }
            }
            return null;
          };
          
          const foundRequestsArray = findArrayInObject(response);
          if (foundRequestsArray) {
            console.log('Found requests array through deep search:', foundRequestsArray);
            setRequests(foundRequestsArray);
          } else {
            // Check for inconsistent pagination vs data
            const paginationTotal = response.pagination?.total || 0;
            if (paginationTotal > 0) {
              console.warn('API pagination indicates records exist but no array found');
              setRequests([]);
            } else {
              console.warn('No requests array found in API response');
              setRequests([]);
            }
          }
        } else {
          console.warn('Unexpected API response format');
          setRequests([]);
        }
      } else if (user?.role === 'donor') {
        // If we're a donor, get requests for our listings instead
        const response = await requestService.getRequestsForMyListings();
        console.log('Donor requests for my listings:', response);

        if (response?.requests && Array.isArray(response.requests)) {
          setRequests(response.requests);
        } else if (response?.results && Array.isArray(response.results)) {
          setRequests(response.results);
        } else if (Array.isArray(response)) {
          setRequests(response);
        } else {
          console.warn('No requests array found in API response for donor');
          setRequests([]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch requests:', err);
      setError('Failed to load your requests. Please try again later.');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [user]);
  
  // Load requests on component mount
  useEffect(() => {
    if (user) {
      fetchRequests();
    }
  }, [user, fetchRequests]);

  // Handle request cancellation
  const handleCancelRequest = useCallback(async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this request? This action cannot be undone.')) {
      return;
    }
    try {
      await requestService.cancelRequest(requestId);
      // Update the request status in the local state
      setRequests(requests.map(req => req.id === requestId ? { ...req, status: 'canceled' } : req));
    } catch (err) {
      setError('Failed to cancel request. Please try again.');
      console.error(err);
    }
  }, [requests]);

  // Format date for display
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  }, []);

  // Calculate time remaining until expiry
  const getTimeRemaining = useCallback((expiryDate) => {
    if (!expiryDate) return null;
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'Expired';
    } else if (diffDays === 0) {
      return 'Expires today';
    } else if (diffDays === 1) {
      return 'Expires tomorrow';
    } else if (diffDays < 7) {
      return `${diffDays} days remaining`;
    } else {
      return `${Math.floor(diffDays / 7)} weeks remaining`;
    }
  }, []);// Ensure requests is always an array
  const requestsArray = Array.isArray(requests) ? requests : [];
  
  // Filter requests based on active tab
  const filteredRequests = activeTab === 'all' 
    ? requestsArray 
    : requestsArray.filter(req => req.status === activeTab);

  // If user is not a recipient, show access denied
  if (user && user.role !== 'recipient') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">Access Denied</h1>
          <p className="mt-2 text-lg text-gray-600">
            This page is only accessible to recipient accounts.
          </p>
          <Link to="/" className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Manage Your Food Requests
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            View and manage your food donation requests
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <Link
            to="/listings"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            Browse Food Listings
          </Link>
        </div>
      </div>
      
      {/* Status filter tabs */}
      <div className="mt-4 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('all')}
            className={`${
              activeTab === 'all'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            All Requests
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`${
              activeTab === 'pending'
                ? 'border-yellow-500 text-yellow-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Pending
          </button>
          <button
            onClick={() => setActiveTab('approved')}
            className={`${
              activeTab === 'approved'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Approved
          </button>
          <button
            onClick={() => setActiveTab('fulfilled')}
            className={`${
              activeTab === 'fulfilled'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Fulfilled
          </button>
          <button
            onClick={() => setActiveTab('rejected')}
            className={`${
              activeTab === 'rejected'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Rejected
          </button>
        </nav>
      </div>
      
      {/* Error and loading states */}
      {error && <ErrorAlert message={error} />}
      
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="large" />
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md p-6 mt-6 text-center">
          <p className="text-gray-500">
            {activeTab === 'all'
              ? "You haven't made any requests yet. Browse available food to make a request."
              : `You don't have any ${activeTab} requests.`
            }
          </p>
          <Link
            to="/listings"
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
          >
            Find Available Food
          </Link>
        </div>
      ) : (
        <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
          <ul className="divide-y divide-gray-200">
            {filteredRequests.map((request) => (
              <li key={request.id} className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col md:flex-row md:items-center">
                    <p className="text-sm font-medium text-primary-600 truncate">
                      {request.listing_title}
                    </p>
                    <div className="md:ml-2 flex flex-shrink-0">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                          request.status === 'approved' ? 'bg-green-100 text-green-800' : 
                          request.status === 'fulfilled' ? 'bg-blue-100 text-blue-800' :
                          request.status === 'canceled' ? 'bg-gray-100 text-gray-800' :
                          'bg-red-100 text-red-800'}`}>
                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      </span>
                    </div>
                  </div>
                  <div className="ml-2 flex-shrink-0 flex">
                    <Link 
                      to={`/requests/${request.id}`}
                      className="mr-2 font-medium text-sm text-primary-600 hover:text-primary-900"
                    >
                      View Details
                    </Link>
                    {request.status === 'pending' && (
                      <button
                        onClick={() => handleCancelRequest(request.id)}
                        className="font-medium text-sm text-red-600 hover:text-red-900"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-2 sm:flex sm:justify-between">
                  <div className="sm:flex">
                    <p className="flex items-center text-sm text-gray-500">
                      <span>Quantity: {request.quantity} {request.quantity_unit}</span>
                    </p>
                    <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                      <span>Donor: {request.donor_name || 'Unknown'}</span>
                    </p>
                  </div>
                  <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                    <p>
                      Requested on {formatDate(request.created_at)}
                    </p>
                  </div>
                </div>
                {request.status === 'approved' && request.listing_expiry && (
                  <div className="mt-2 text-sm text-orange-600">
                    <p>Pickup before: {formatDate(request.listing_expiry)} ({getTimeRemaining(request.listing_expiry)})</p>
                  </div>
                )}
                {request.pickup_time && (
                  <div className="mt-1 text-sm text-green-600">
                    <p>Scheduled pickup: {formatDate(request.pickup_time)}</p>
                  </div>
                )}
                {request.notes && (
                  <div className="mt-2 text-sm text-gray-500">
                    <p className="italic">"{request.notes}"</p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default ManageRequestsPage;
